import React from 'react';
import { Link } from 'react-router-dom';
import Slider from '../components/slider';
import BigBlogEntry from '../components/BigBlogEntry';
import LatestAndTrending from '../components/LatestAndTrending';
import CategorySection from '../components/CategorySection';
import SimpleBlogEntries from '../components/SimpleBlogEntries';
import TrendingVideos from '../components/TrendingVideos';
import M365UpdateList from '../components/M365UpdateList';
import TeaserBlogEntry from '../components/TeaserBlogEntry';
import type { M365SortField } from '../types/m365';
import type { M365ReleaseItem } from '../types/m365';
import type { SimpleBlogItem } from '../utils/BlogListProvider';
import { useState, useEffect } from 'react';
import { MSRoadMapManager } from '../utils/MSRoadMapManager';
import { AIBlogContent } from '../utils/AIBlogContent';
import { Utilities } from '../utils/Utilities';
import MSRCInformation from '../components/MSRCInformation';
import M365ServiceStatusGrid from '../components/M365ServiceStatusGrid';
import { BloggerManager,BloggerPage } from '../utils/BloggerManager';
import CalenderEventsCalendar from '../components/CalenderEventsCalendar';
import LOGO_PLACEHOLDER from '../assets/images/icons8-blog-150.png';

interface DisplayInformation {
    NewsAndFeaturePosts: string[];
    DeveloperTopics: string[];
    DeveloperHighlightVideo: string;
    FeatureBlogID: string;
}


function Landing() {
    const [m365Feed, setM365Feed] = useState<M365ReleaseItem[]>([]);
    const [loadingM365RoadMap, setLoadingM365RoadMap] = useState(true);
    const [errorM365RoadMap, setErrorM365RoadMap] = useState<string | null>(null);
    const [updateContentPosts, setUpdateContentPosts] = useState<string[] | null>(null);
    const [featureBlogID, setFeatureBlogID] = useState<string | undefined>(undefined);
    const [developerHighlightVideo, setDeveloperHighlightVideo] = useState<string | undefined>(undefined);
    const [aiBlogEntries, setAiBlogEntries] = useState<SimpleBlogItem[]>([]);

    useEffect(() => {
        let cancelled = false;
        setLoadingM365RoadMap(true);
        setErrorM365RoadMap(null);
        MSRoadMapManager.getInstance().fetchM365Releases()
            .then((data) => {
                if (!cancelled) 
                    setM365Feed(data);
            })
            .catch((err) => {
                if (!cancelled) {
                    setErrorM365RoadMap(err instanceof Error ? err.message : 'Failed to load releases');
                    setM365Feed([]);
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingM365RoadMap(false);
            });
        

        BloggerManager.getInstance().getPageFromPost('1663315143208154609').then((page: BloggerPage) => {
           if (page) {
            const infoData :DisplayInformation = JSON.parse(page.content);            
            setUpdateContentPosts(infoData.NewsAndFeaturePosts);
            setFeatureBlogID(infoData.FeatureBlogID);
            setDeveloperHighlightVideo(infoData.DeveloperHighlightVideo);
           } 
        });

        AIBlogContent.getInstance().GetData(1, 3)
            .then((data) => { if (!cancelled) setAiBlogEntries(data); })
            .catch(() => { if (!cancelled) setAiBlogEntries([]); });

        return () => { cancelled = true; };
    }, []);

    return (
        <>
            {/* Slider Section */}
            <div className="container">
                <div className="row">  
                    <div className="col-lg-8">
                        <Slider />
                        <div className="recent-posts-widget widget-item ms-2 me-2">
                            <h2 className="widget-title">Last from my Copilot AI Blog</h2>
                            <div className="d-flex">
                            {aiBlogEntries.map((item) => {
                                const dateObj = item.created ?? item.modified;
                                const date = dateObj
                                    ? Utilities.formatDate(dateObj.toISOString())
                                    : '—';
                                return (
                                    <TeaserBlogEntry
                                        id={item.id}
                                        key={item.id}
                                        imageUrl={item.logoUrl?.trim() || LOGO_PLACEHOLDER}
                                        imageAlt={item.title}
                                        category={''}
                                        date={date}
                                        title={item.title}
                                        linkTo={item.blogUrl?.trim() || '/aiblog'}
                                    />
                                );
                            })}                            
                            </div>                            
                        </div>
                        <div className="text-end"><a href="/aiblog">All posts</a></div>
                    </div>
                    <div className="col-lg-4 pt-3">
                        <CalenderEventsCalendar
                            date={new Date()}                        
                            outputStyle="list"
                        />
                        <div className=' text-end'><a href="/calendar">All events</a></div>                        
                    </div>
                </div>
            </div>
            

            {/* Trending Category Section */}

            <CategorySection sectionId="trending-category" categoryName="Latest from me" className="trending-category section" 
                    internalSectionId="roadmap"
                    subTitle="All videos"
                    seeAllLink="/m365updates">
                <div className="container">
                    <div className="container">
                        <div className="row g-5">
                            <div className="col-lg-4">
                                <BigBlogEntry blogID={developerHighlightVideo} />
                            </div>

                            <div className="col-lg-8">
                                <div className="row g-5">
                                    <div className="col-lg-4 border-start custom-border">
                                        <SimpleBlogEntries
                                            count={3}
                                            skip={1}
                                            sortOrderField="published"
                                            sortDirection="desc"
                                            />
                                    </div>
                                    <div className="col-lg-4 border-start custom-border">
                                        <SimpleBlogEntries
                                            count={3}
                                            skip={4}
                                            sortOrderField="published"
                                            sortDirection="desc"
                                            />                                        
                                    </div>

                                    {/* Trending Section */}
                                    <div className="col-lg-4">
                                        <TrendingVideos count={5} />                                        
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </CategorySection>

            <CategorySection sectionId="health-category" categoryName="M365 Service Status" 
                    internalSectionId="health"
                    seeAllLink="">
                <div className="row">
                    <div className="col-lg-8">
                        <div className="alert alert-primary d-flex" role="alert">
                            <div><i className="bi bi-info-circle fs-3 pe-3"></i></div>
                            
                            <div className='flex-fill'>
                                This is the current service status from my example tenant. Please note that this is not the official service status from Microsoft.  It is only for demonstration purposes.
                                Check your own service status at <a href="https://admin.cloud.microsoft/?source=applauncher#/servicehealth" target="_blank" rel="noopener noreferrer">Service health</a>
                            </div>
                        </div>
                        
                        <M365ServiceStatusGrid />
                    </div>
                    <div className="col-lg-4">
                        <div className="important-link-widget widget-item m365-status-links">
                            <h3 className="widget-title">More information</h3>
                            <div className="m365-status-links__list">
                                <a
                                    href="https://go.microsoft.com/fwlink/p/?linkid=2024339"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="m365-status-links__item"
                                >
                                    <span className="m365-status-links__icon">
                                        <i className="bi bi-info-square" />
                                    </span>
                                    <div className="m365-status-links__content">
                                        <span className="m365-status-links__title">Your Service Health</span>
                                        <span className="m365-status-links__desc">View the issues and health status of all services that are available with your current subscriptions.</span>
                                    </div>
                                    <i className="bi bi-box-arrow-up-right m365-status-links__external" />
                                </a>
                                <a
                                    href="https://status.cloud.microsoft/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="m365-status-links__item"
                                >
                                    <span className="m365-status-links__icon">
                                        <i className="bi bi-activity" />
                                    </span>
                                    <div className="m365-status-links__content">
                                        <span className="m365-status-links__title">Public M365 Status Dashboard</span>
                                        <span className="m365-status-links__desc">Real-time status of Microsoft 365 services, outages, and incidents.</span>
                                    </div>
                                    <i className="bi bi-box-arrow-up-right m365-status-links__external" />
                                </a>
                                <a
                                    href="https://developer.microsoft.com/en-us/graph/graph-explorer?request=admin%2FserviceAnnouncement%2FhealthOverviews&version=v1.0"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="m365-status-links__item"
                                >
                                    <span className="m365-status-links__icon">
                                        <i className="bi bi-code-slash" />
                                    </span>
                                    <div className="m365-status-links__content">
                                        <span className="m365-status-links__title">Graph API Explorer</span>
                                        <span className="m365-status-links__desc">Explore service health data via Microsoft Graph API.</span>
                                    </div>
                                    <i className="bi bi-box-arrow-up-right m365-status-links__external" />
                                </a>
                                <a
                                    href="https://x.com/MSFT365Status"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="m365-status-links__item"
                                >
                                    <span className="m365-status-links__icon">
                                        <i className="bi bi-code-slash" />
                                    </span>
                                    <div className="m365-status-links__content">
                                        <span className="m365-status-links__title">@MSFT365status</span>
                                        <span className="m365-status-links__desc">Follow at @MSFT365status on X (Twitter) to see information on certain events.</span>
                                    </div>
                                    <i className="bi bi-box-arrow-up-right m365-status-links__external" />
                                </a>     
                                <a
                                    href="https://learn.microsoft.com/en-us/microsoft-365/enterprise/view-service-health"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="m365-status-links__item"
                                >
                                    <span className="m365-status-links__icon">
                                        <i className="bi bi-book" />
                                    </span>
                                    <div className="m365-status-links__content">
                                        <span className="m365-status-links__title">Microsoft 365 service health (Learn)</span>
                                        <span className="m365-status-links__desc">How to check Microsoft 365 service health from your tenant.</span>
                                    </div>
                                    <i className="bi bi-box-arrow-up-right m365-status-links__external" />
                                </a>                                                           
                            </div>
                        </div>
                    </div>                    
                </div>
            </CategorySection>

            {/* Roadmap information Section */}
            <CategorySection sectionId="roadmap-category" categoryName="Upcoming Features" 
                    internalSectionId="roadmap"
                    seeAllLink="/m365updates">
                    <div className="row">
                        <div className="col-md-9">
                            <BigBlogEntry viewMode="BigTile" blogID={featureBlogID} />

                            <div className='row'>
                                <h3>Common Vulnerability Reporting</h3>
                                <div className='col-lg-12'><MSRCInformation count={6} /></div>
                                
                                <div className='pt-2'>
                                    <a href="https://msrc.microsoft.com/" target="_blank" rel="noopener noreferrer">More information</a>
                                    <i className="bi bi-box-arrow-up-right m365-status-links__external ps-1" />
                                </div>                            
                            </div>

                            <div className="row">
                                <div className="col-lg-12 pt-4">
                                    <SimpleBlogEntries
                                                count={0}
                                                skip={0}
                                                sortOrderField="published"
                                                sortDirection="desc"
                                                layout="grid"
                                                blogIDs={updateContentPosts??[]}
                                                />
                                </div>
                                {/** 
                                <div className="col-lg-8">
                                    <div className="post-list">
                                        <Link to="/blog-details">
                                            <img src="assets/img/post-landscape-2.jpg" alt="" className="img-fluid" />
                                        </Link>
                                        <div className="post-meta">
                                            <span className="date">Culture</span> <span className="mx-1">•</span> <span>Jul 5th '22</span>
                                        </div>
                                        <h2 className="mb-2">
                                            <Link to="/blog-details">How to Avoid Distraction and Stay Focused During Video Calls?</Link>
                                        </h2>
                                        <span className="author mb-3 d-block">Jenny Wilson</span>
                                        <p className="mb-4 d-block">Lorem ipsum dolor sit, amet consectetur adipisicing elit. Vero temporibus repudiandae, inventore pariatur numquam cumque possimus</p>
                                    </div>
                                </div>
                                */}
                            </div>
                        </div>

                        <div className="col-md-3">
                            <M365UpdateList
                                items={m365Feed}
                                loading={loadingM365RoadMap}     
                                error={errorM365RoadMap}
                                maxItems={5}
                                initialStatusFilter={['Rolling out']}
                                sortField={'modified' as M365SortField}
                                viewMode='compact'
                                />
                        </div>
                    </div>
            </CategorySection>
        </>
    );
}

export default Landing;
